'use client';
import { readStoredLocale } from "@/i18n/LocaleProvider";
import { responseLanguageForLocale } from "@/i18n/locales";
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveAuthTokens } from '@/app/contexts';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import { fetchChatHistory, postAssistantChat } from '@/shared/cors-assistant-api';
import { getTokenInfo, getUserEmailForHeaders } from '@/shared/functions';
import {
  CHAT_HISTORY_FIRST_POLL_DELAY_MS,
  CHAT_HISTORY_POLL_INTERVAL_MS,
  parsePendingTurnIdentityFromAssistantResponse,
} from '@/shared/pending-chat-turn';
import { buildAssistantTurns, type AssistantTurn } from '../lib/assistant-thread';

/** Stop polling a turn after this long; the mobile agent runs for minutes, not seconds. */
const POLL_TIMEOUT_MS = 15 * 60 * 1000;

export interface MobileAssistantState {
  turns: AssistantTurn[];
  /** True from submit until the answer settles. */
  busy: boolean;
  error: string | null;
  ready: boolean;
  ask: (question: string) => void;
}

/**
 * Assistant conversation scoped to one acquisition.
 *
 * `POST /chat` only acknowledges the request — the answer is written to chat
 * history by a background agent — so this posts, then polls history until the
 * turn settles. The optimistic turn is held locally until the server row for it
 * appears, otherwise the panel would look empty for the first six seconds of a
 * multi-minute analysis.
 */
export function useMobileAssistant(args: {
  acquisitionId: number;
  caseName: string | null | undefined;
}): MobileAssistantState {
  const t = useTimePilotT();
  const { acquisitionId, caseName } = args;
  const { idToken, realAccessToken } = useReactiveAuthTokens();

  const cellId = useMemo(() => resolveCellIdFromIdToken(idToken), [idToken]);
  const userId = useMemo(
    () => getUserEmailForHeaders(getTokenInfo(idToken ?? ''), null),
    [idToken],
  );

  const selectedCase = caseName?.trim() || '';
  const ready = Boolean(cellId && realAccessToken && userId && selectedCase);

  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [pendingTurn, setPendingTurn] = useState<AssistantTurn | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  // A poll in flight for a previous acquisition must not write into the panel
  // after the user has moved on.
  const runIdRef = useRef(0);
  useEffect(() => {
    runIdRef.current += 1;
    setTurns([]);
    setPendingTurn(null);
    setBusy(false);
    setError(null);
    sessionIdRef.current = null;
  }, [acquisitionId, selectedCase]);

  const loadHistory = useCallback(async (): Promise<AssistantTurn[]> => {
    const rows = await fetchChatHistory({
      cellId: cellId as string,
      accessToken: realAccessToken as string,
      idToken,
      body: { userId, caseId: selectedCase },
    });
    return buildAssistantTurns(rows);
  }, [cellId, realAccessToken, idToken, userId, selectedCase]);

  // Load prior mobile analyses so the panel opens with context rather than blank.
  useEffect(() => {
    if (!ready) return;
    const runId = runIdRef.current;
    let cancelled = false;
    loadHistory()
      .then((loaded) => {
        if (cancelled || runIdRef.current !== runId) return;
        setTurns(loaded);
        sessionIdRef.current = loaded[loaded.length - 1]?.sessionId ?? null;
      })
      .catch(() => {
        // A failed history load leaves the panel usable for new questions;
        // surfacing it as an error would block a working feature on a stale one.
      });
    return () => {
      cancelled = true;
    };
  }, [ready, loadHistory]);

  // Closing the dock unmounts this hook. Bump the run id so an in-flight poll
  // stops calling setState and fetching history.
  useEffect(() => {
    const runIdRefAtMount = runIdRef;
    return () => {
      runIdRefAtMount.current += 1;
    };
  }, []);

  const ask = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || !ready || busy) return;

      const runId = ++runIdRef.current;
      setError(null);
      setBusy(true);
      setPendingTurn({
        epochTimeSecs: -Date.now(),
        question: trimmed,
        answer: '',
        messages: [],
        citations: [],
        pending: true,
        failed: false,
      });

      const pollUntilSettled = async (epochTimeSecs: number | null) => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let delay = CHAT_HISTORY_FIRST_POLL_DELAY_MS;

        while (Date.now() < deadline) {
          await sleep(delay);
          delay = CHAT_HISTORY_POLL_INTERVAL_MS;
          if (runIdRef.current !== runId) return;

          let loaded: AssistantTurn[];
          try {
            loaded = await loadHistory();
          } catch {
            // Transient failures are expected while the agent runs; keep polling
            // rather than discarding an analysis that may already be minutes in.
            continue;
          }
          if (runIdRef.current !== runId) return;

          setTurns(loaded);

          const turn =
            epochTimeSecs != null
              ? loaded.find((t) => t.epochTimeSecs === epochTimeSecs)
              : loaded[loaded.length - 1];

          // The local placeholder is only redundant once the server row exists —
          // that row is what carries the agent's progress steps.
          if (turn) setPendingTurn(null);
          if (turn && !turn.pending) {
            setBusy(false);
            return;
          }
        }

        if (runIdRef.current !== runId) return;
        setBusy(false);
        setPendingTurn(null);
        setError(
          t("viewers.theAnalysisIsTakingLongerThanExpected"),
        );
      };

      void (async () => {
        try {
          const responseLanguage = responseLanguageForLocale(readStoredLocale());
          const result = await postAssistantChat({
            cellId: cellId as string,
            accessToken: realAccessToken as string,
            idToken,
            body: {
              query: trimmed,
              mobile: true,
              requestFeedback: true,
              mobileAcquisitionId: acquisitionId,
              deepDive: false,
              threatReport: false,
              selectedCase,
              userId,
              sessionId: sessionIdRef.current,
              files: [],
              scopes: [],
              ...(responseLanguage ? { responseLanguage } : {}),
              excludedFiles: [],
              excludedScopes: [],
            },
          });

          if (runIdRef.current !== runId) return;

          const sessionId = result?.sessionId;
          if (typeof sessionId === 'string' && sessionId) sessionIdRef.current = sessionId;

          const identity = parsePendingTurnIdentityFromAssistantResponse(result);
          await pollUntilSettled(identity?.epochTimeSecs ?? null);
        } catch (caught) {
          if (runIdRef.current !== runId) return;
          setError((caught as Error).message || 'The assistant could not be reached.');
          setBusy(false);
          setPendingTurn(null);
        }
      })();
    },
    [ready, busy, cellId, realAccessToken, idToken, acquisitionId, selectedCase, userId, loadHistory],
  );

  const visibleTurns = useMemo(
    () => (pendingTurn ? [...turns, pendingTurn] : turns),
    [turns, pendingTurn],
  );

  return { turns: visibleTurns, busy, error, ready, ask };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
